import ExtensionIcon from "@mui/icons-material/Extension";
import GridOnIcon from "@mui/icons-material/GridOn";
import MessageIcon from "@mui/icons-material/Message";
import SearchIcon from "@mui/icons-material/Search";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StoryReferenceWithGrid from "../../admin/story/StoryReferenceWithGrid";
import slidingPuzzleQuestIcon from "../../assets/app-icons/sliding-puzzle-quest-app-icon.png";
import HomeService from "../../services/HomeService";
import { trackEvent } from "../../services/GoogleAnalytics";
import CrossBrowseGridPreview from "../browse-puzzles/CrossBrowseGridPreview";
import GridBrowseGridPreview from "../browse-puzzles/GridBrowseGridPreview";
import { getHomePuzzleLandingFeaturedMeta } from "../browse-puzzles/homePuzzleLandingUtils";
import {
    buildCrossBrowseGridPreviewFromRow,
    buildCrosswordClueTeaserEntries,
    buildGridBrowseGridPreviewFromRow,
} from "../browse-puzzles/homePuzzleRoUtils";
import { getSlidingLandingFeaturedMeta } from "../browse-sliding/slidingBrowseUtils";
import { getJigsawLandingFeaturedMeta } from "../puzzles/jigsaw/jigsawBrowseUtils";
import JigsawColorBasketPlayService from "../puzzles/jigsaw/JigsawColorBasketPlayService";
import JigsawDieLineOverlay from "../puzzles/jigsaw/JigsawDieLineOverlay";
import {
    isJigsawPremiumTier,
    isSlidingPremiumTier,
} from "../puzzles/common/puzzleTierAccess";
import Logger from "../../services/Logger";
import { usePagesTranslation } from "../locales";
import { SHOW_SLIDING_APP_STORE_BADGE, SHOW_SLIDING_GOOGLE_PLAY_BADGE, SHOW_JIGSAW_GOOGLE_PLAY_BADGE, JIGSAW_MUSE_GOOGLE_PLAY_URL } from "../../config/nativeAppsVisibility";
import AppStoreBadgeLink from "../components/AppStoreBadgeLink";
import AppStorePromoPill from "../components/AppStorePromoPill";
import GooglePlayBadgeLink from "../components/GooglePlayBadgeLink";
import CrosswordClueTeaserDescription from "./CrosswordClueTeaserDescription";
import LandingColorBasketCard from "./LandingColorBasketCard";
import LandingFeaturedPuzzleCard from "./LandingFeaturedPuzzleCard";
import LandingWordWheelSection from "./LandingWordWheelSection";

const JIGSAW_MUSE_APP_ICON = `${process.env.PUBLIC_URL || ""}/ad/jigsaw-muse-app-icon-512.png`;

const DEFAULT_BASKET_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** Landing sliding / jigsaw featured API responses — reuse for 3 hours. */
const LANDING_FEATURED_CACHE_TTL_MS = 3 * 60 * 60 * 1000;
const LANDING_FEATURED_CACHE_PREFIX = "landing.featured.v1.";

function landingFeaturedCacheKey(part, contentLanguage) {
    const lang = (contentLanguage || "english").toLowerCase();
    return `${LANDING_FEATURED_CACHE_PREFIX}${part}.${lang}`;
}

function readLandingFeaturedCache(key) {
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        const savedAt = Number(parsed.savedAt);
        if (!Number.isFinite(savedAt) || Date.now() - savedAt > LANDING_FEATURED_CACHE_TTL_MS) {
            window.localStorage.removeItem(key);
            return null;
        }
        return parsed.data;
    } catch {
        return null;
    }
}

function writeLandingFeaturedCache(key, data) {
    try {
        window.localStorage.setItem(
            key,
            JSON.stringify({ savedAt: Date.now(), data }),
        );
    } catch {
        /* quota / private mode — ignore */
    }
}

function preferBasketCount(allowed, meta) {
    const list = Array.isArray(allowed) && allowed.length > 0 ? allowed : DEFAULT_BASKET_COUNTS;
    const freeMax = Number(meta?.freeMaxCount);
    if (Number.isFinite(freeMax) && freeMax >= 2) {
        if (list.includes(freeMax)) return freeMax;
        const freeAllowed = list.filter((n) => n <= freeMax);
        if (freeAllowed.length > 0) return freeAllowed[freeAllowed.length - 1];
    }
    return list[0];
}

const SECTION_HEADLINE_SX = {
    textAlign: "center",
    fontWeight: 800,
    fontSize: { xs: "1.65rem", sm: "2rem", md: "2.25rem" },
    lineHeight: 1.2,
    letterSpacing: "-0.03em",
    mb: { xs: 2.5, sm: 3 },
    background: "linear-gradient(118deg, #1e3a2f 0%, #2e7d32 42%, #3BA666 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
};

const CARD_FEED_SX = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
};

const SECTION_SX = {
    mb: { xs: 4, sm: 5 },
    maxWidth: 920,
    mx: "auto",
    width: "100%",
};

function renderFeaturedCard(card, { t, moreLabel, lang }) {
    return (
        <LandingFeaturedPuzzleCard
            key={card.key}
            puzzleKind={card.puzzleKind}
            badgeLabel={card.badgeLabel}
            isFreeTier={card.isFreeTier}
            title={card.title}
            gridSize={card.gridSize}
            levelLabel={card.levelLabel}
            publishedAt={card.publishedAt}
            subtitle={card.subtitle ?? t(card.subtitleKey)}
            subtitleDense={card.subtitleDense ?? false}
            playLabel={card.playLabel}
            playAria={t(card.playAriaKey, { title: card.title })}
            onPlay={card.onPlay}
            morePath={card.morePath}
            moreLabel={moreLabel}
            placeholderIcon={card.placeholderIcon}
            preview={card.preview}
            appStoreLink={card.appStoreLink ?? null}
            lang={lang}
            colorBaskets={card.colorBaskets ?? null}
        />
    );
}

const WORD_PUZZLE_SLOTS = [
    {
        id: "crossword",
        puzzleKind: "crossword",
        puzzleType: "CROSSWORD",
        subtitleKey: "landing.crossword-free-teaser-subtitle",
        playAriaKey: "landing.crossword-hero-play-aria",
        morePath: "/puzzles/crossword",
        trackEventName: "landing_crossword_hero_play",
        placeholderIcon: GridOnIcon,
    },
    {
        id: "hidden-message",
        puzzleKind: "hidden_message",
        puzzleType: "HIDDEN_MESSAGE",
        subtitleKey: "landing.hidden-message-free-teaser-subtitle",
        playAriaKey: "landing.hidden-message-hero-play-aria",
        morePath: "/puzzles/hidden-message",
        trackEventName: "landing_hidden_message_hero_play",
        placeholderIcon: MessageIcon,
    },
    {
        id: "word-search",
        puzzleKind: "word_search",
        puzzleType: "WORD_SEARCH",
        subtitleKey: "landing.word-search-free-teaser-subtitle",
        playAriaKey: "landing.word-search-hero-play-aria",
        morePath: "/puzzles/word-search",
        trackEventName: "landing_word_search_hero_play",
        placeholderIcon: SearchIcon,
    },
];

function formatDifficultyLabel(level, t) {
    const key = String(level || "").toUpperCase();
    if (key === "EASY") return t("puzzle.card.difficulty-easy");
    if (key === "MEDIUM") return t("puzzle.card.difficulty-medium");
    if (key === "HARD") return t("puzzle.card.difficulty-hard");
    return level || t("puzzle.card.difficulty-medium");
}

/** Newest published row for this type (server orders by published_at desc). */
async function fetchHomePuzzleFeatured(puzzleType, contentLanguage) {
    const page = await HomeService.getHomePuzzlePage({
        page: 0,
        pageSize: 4,
        language: contentLanguage,
        puzzleType,
        accessFilter: "all",
    });
    const rows = page.content ?? [];
    for (const row of rows) {
        const meta = getHomePuzzleLandingFeaturedMeta(row, contentLanguage);
        if (meta) return { meta, row };
    }
    return null;
}

function buildWordPuzzleFeaturedPreview(row, puzzleType) {
    if (!row) return null;
    if (puzzleType === "CROSSWORD") {
        const preview = buildCrossBrowseGridPreviewFromRow(row);
        if (!preview) return null;
        return (
            <CrossBrowseGridPreview
                grid={preview.grid}
                filledCoordinates={preview.filledCoordinates}
                words={preview.words}
            />
        );
    }
    if (puzzleType === "WORD_SEARCH" || puzzleType === "HIDDEN_MESSAGE") {
        const preview = buildGridBrowseGridPreviewFromRow(row, puzzleType);
        if (!preview) return null;
        return (
            <GridBrowseGridPreview
                grid={preview.grid}
                filledCoordinates={preview.filledCoordinates}
                gridSize={preview.gridSize}
            />
        );
    }
    return null;
}

function getGroupStoryId(group) {
    if (!group) return null;
    const id = group.storyId ?? group.story?.id;
    return typeof id === "string" ? id.trim() : null;
}

function pickJigsawGroupForLanding(slidingGroup, jigsawGroups) {
    const list = Array.isArray(jigsawGroups) ? jigsawGroups.filter(Boolean) : [];
    if (list.length === 0) return null;

    const slidingStoryId = getGroupStoryId(slidingGroup);
    if (slidingStoryId) {
        const different = list.find((group) => getGroupStoryId(group) !== slidingStoryId);
        if (different) return different;
    }

    return list[1] ?? list[0];
}

function StoryImagePreview({ imageUrl }) {
    if (!imageUrl) return null;
    return (
        <Box
            component="img"
            src={imageUrl}
            alt=""
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
    );
}

function JigsawPreview({ imageUrl, pieces, svgPathFallback }) {
    if (!imageUrl) return null;
    return (
        <Box sx={{ position: "relative", width: "100%", height: "100%", bgcolor: "grey.900" }}>
            <Box
                component="img"
                src={imageUrl}
                alt=""
                sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
            />
            <JigsawDieLineOverlay
                pieces={pieces}
                svgPathFallback={svgPathFallback}
                opacity={0.85}
                zIndex={1}
                strokeWidth={2.5}
            />
        </Box>
    );
}

export default function LandingFeaturedPuzzlesHero({ contentLanguage }) {
    const { t, lang } = usePagesTranslation();
    const navigate = useNavigate();
    const [slidingGroup, setSlidingGroup] = useState(null);
    const [jigsawGroup, setJigsawGroup] = useState(null);
    const [jigsawColorBaskets, setJigsawColorBaskets] = useState([]);
    const [wordPuzzles, setWordPuzzles] = useState({});
    const [slidingLoading, setSlidingLoading] = useState(true);
    const [jigsawLoading, setJigsawLoading] = useState(true);
    const [wordPuzzlesLoading, setWordPuzzlesLoading] = useState(true);

    const playNowLabel = t("landing.play-now");
    const solveLabel = t("landing.featured-solve", "Solve");
    const moreLabel = t("landing.puzzle-of-day-more");

    useEffect(() => {
        let cancelled = false;
        const slidingKey = landingFeaturedCacheKey("sliding", contentLanguage);
        const jigsawKey = landingFeaturedCacheKey("jigsawFeatured", contentLanguage);
        const cachedSliding = readLandingFeaturedCache(slidingKey);
        const cachedJigsawFeatured = readLandingFeaturedCache(jigsawKey);

        if (cachedSliding !== null || cachedJigsawFeatured !== null) {
            if (cachedSliding !== null) setSlidingGroup(cachedSliding);
            if (cachedJigsawFeatured !== null) {
                setJigsawGroup(
                    pickJigsawGroupForLanding(
                        cachedSliding !== null ? cachedSliding : null,
                        cachedJigsawFeatured,
                    ),
                );
            }
            if (cachedSliding !== null) setSlidingLoading(false);
            if (cachedJigsawFeatured !== null) setJigsawLoading(false);
        } else {
            setSlidingLoading(true);
            setJigsawLoading(true);
        }

        const needSliding = cachedSliding === null;
        const needJigsaw = cachedJigsawFeatured === null;
        if (!needSliding && !needJigsaw) {
            return () => {
                cancelled = true;
            };
        }

        (async () => {
            try {
                const [slidingData, jigsawFeatured] = await Promise.all([
                    needSliding
                        ? HomeService.getHomeFreeLatest({ language: contentLanguage })
                        : Promise.resolve(cachedSliding),
                    needJigsaw
                        ? HomeService.getHomeJigsawFreeFeatured({
                            language: contentLanguage,
                            count: 2,
                        })
                        : Promise.resolve(cachedJigsawFeatured),
                ]);
                if (cancelled) return;
                if (needSliding) {
                    writeLandingFeaturedCache(slidingKey, slidingData ?? null);
                    setSlidingGroup(slidingData);
                    setSlidingLoading(false);
                }
                if (needJigsaw) {
                    writeLandingFeaturedCache(jigsawKey, jigsawFeatured ?? null);
                }
                // Re-pick when either side refreshed so jigsaw stays distinct from sliding.
                setJigsawGroup(
                    pickJigsawGroupForLanding(
                        needSliding ? slidingData : cachedSliding,
                        needJigsaw ? jigsawFeatured : cachedJigsawFeatured,
                    ),
                );
                setJigsawLoading(false);
            } catch (e) {
                if (!cancelled) {
                    Logger.error("Landing featured sliding/jigsaw failed", e);
                    if (needSliding) setSlidingLoading(false);
                    if (needJigsaw) setJigsawLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [contentLanguage]);

    useEffect(() => {
        let cancelled = false;
        setWordPuzzlesLoading(true);
        (async () => {
            const entries = await Promise.all(
                WORD_PUZZLE_SLOTS.map(async ({ puzzleType }) => {
                    const featured = await fetchHomePuzzleFeatured(puzzleType, contentLanguage);
                    return [puzzleType, featured];
                }),
            );
            if (!cancelled) {
                setWordPuzzles(Object.fromEntries(entries));
                setWordPuzzlesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [contentLanguage]);

    const slidingMeta = useMemo(
        () => (slidingGroup ? getSlidingLandingFeaturedMeta(slidingGroup) : null),
        [slidingGroup],
    );

    const jigsawMeta = useMemo(
        () => (jigsawGroup ? getJigsawLandingFeaturedMeta(jigsawGroup) : null),
        [jigsawGroup],
    );

    useEffect(() => {
        const basketJigsawId = jigsawMeta?.basketJigsawId;
        if (!basketJigsawId) {
            setJigsawColorBaskets([]);
            return undefined;
        }
        const basketsKey = `${LANDING_FEATURED_CACHE_PREFIX}jigsawBaskets.${basketJigsawId}`;
        const cachedBaskets = readLandingFeaturedCache(basketsKey);
        if (Array.isArray(cachedBaskets)) {
            setJigsawColorBaskets(cachedBaskets);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            try {
                const meta = await JigsawColorBasketPlayService.getColorBaskets(basketJigsawId);
                if (cancelled) return;
                if (meta?.available === false) {
                    writeLandingFeaturedCache(basketsKey, []);
                    setJigsawColorBaskets([]);
                    return;
                }
                const allowed =
                    Array.isArray(meta?.allowedCounts) && meta.allowedCounts.length > 0
                        ? meta.allowedCounts.map(Number).filter((n) => Number.isFinite(n) && n >= 2)
                        : DEFAULT_BASKET_COUNTS;
                const k = preferBasketCount(allowed, meta);
                const result = await JigsawColorBasketPlayService.getColorBaskets(basketJigsawId, k);
                if (cancelled) return;
                const list = Array.isArray(result?.baskets) ? result.baskets : [];
                writeLandingFeaturedCache(basketsKey, list);
                setJigsawColorBaskets(list);
            } catch (e) {
                if (!cancelled) {
                    Logger.error("Landing featured jigsaw color baskets failed", e);
                    setJigsawColorBaskets([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [jigsawMeta?.basketJigsawId]);

    const handleWordPuzzlePlay = useCallback(
        (meta, trackEventName) => {
            if (!meta?.playPath) return;
            trackEvent(trackEventName, { playId: meta.playId, type: meta.type });
            navigate(meta.playPath);
        },
        [navigate],
    );

    const dailyFreeBadge = t("landing.featured-daily-free-badge", "✨ DAILY FREE PLAY");
    const premiumBadge = t("landing.featured-premium-badge", "Premium");

    const slidingCards = useMemo(() => {
        if (!slidingMeta) return [];
        const slidingFreeTier = !isSlidingPremiumTier(
            slidingMeta.gridSize,
            slidingMeta.level,
        );
        return [
            {
                key: "sliding",
                puzzleKind: "sliding",
                isFreeTier: slidingFreeTier,
                badgeLabel: slidingFreeTier ? dailyFreeBadge : premiumBadge,
                subtitleKey: "landing.sliding-free-teaser-subtitle",
                playAriaKey: "landing.sliding-hero-play-aria",
                morePath: "/puzzles/sliding",
                title: slidingMeta.title,
                gridSize: slidingMeta.gridSize,
                levelLabel: formatDifficultyLabel(slidingMeta.level, t),
                publishedAt: slidingMeta.publishedAt,
                playLabel: playNowLabel,
                appStoreLink:
                    SHOW_SLIDING_APP_STORE_BADGE || SHOW_SLIDING_GOOGLE_PLAY_BADGE ? (
                        <AppStorePromoPill
                            iconSrc={slidingPuzzleQuestIcon}
                            appName="Sliding Puzzle Quest"
                            sx={{ maxWidth: "100%" }}
                        >
                            {SHOW_SLIDING_APP_STORE_BADGE ? (
                                <AppStoreBadgeLink height={40} />
                            ) : null}
                            {SHOW_SLIDING_GOOGLE_PLAY_BADGE ? (
                                <GooglePlayBadgeLink height={40} />
                            ) : null}
                        </AppStorePromoPill>
                    ) : null,
                onPlay: () => {
                    trackEvent("landing_sliding_hero_play", { storyId: slidingMeta.storyId });
                    navigate(`/puzzle/play-sliding/${encodeURIComponent(slidingMeta.storyId)}`);
                },
                placeholderIcon: ViewModuleIcon,
                /** Original picture with grid lines — no scramble, no empty slot (same as select level). */
                preview: slidingMeta.imageUrl ? (
                    <StoryReferenceWithGrid
                        imageUrl={slidingMeta.imageUrl}
                        gridSize={slidingMeta.gridSize}
                        alt=""
                        squareFrame
                        showEmptySlot={false}
                        lineColor="#ffffff"
                    />
                ) : null,
            },
        ];
    }, [slidingMeta, t, navigate, playNowLabel, dailyFreeBadge, premiumBadge]);

    const jigsawCards = useMemo(() => {
        if (!jigsawMeta) return [];
        const jigsawFreeTier = !isJigsawPremiumTier(jigsawMeta.tileSize);
        return [
            {
                key: "jigsaw",
                puzzleKind: "jigsaw",
                isFreeTier: jigsawFreeTier,
                badgeLabel: jigsawFreeTier ? dailyFreeBadge : premiumBadge,
                subtitleKey: "landing.jigsaw-free-teaser-subtitle",
                playAriaKey: "landing.jigsaw-hero-play-aria",
                morePath: "/puzzles/jigsaw",
                title: jigsawMeta.title,
                gridSize: jigsawMeta.tileSize,
                levelLabel: formatDifficultyLabel(jigsawMeta.level, t),
                publishedAt: jigsawMeta.publishedAt,
                playLabel: playNowLabel,
                appStoreLink: SHOW_JIGSAW_GOOGLE_PLAY_BADGE ? (
                    <AppStorePromoPill
                        iconSrc={JIGSAW_MUSE_APP_ICON}
                        appName="Jigsaw Muse"
                        tagline="Unwind with Beautiful Puzzles"
                        sx={{ maxWidth: "100%" }}
                    >
                        <GooglePlayBadgeLink
                            height={40}
                            href={JIGSAW_MUSE_GOOGLE_PLAY_URL}
                            ariaLabel={t(
                                "landing.jigsaw-google-play-badge-aria",
                                "Get Jigsaw Muse on Google Play (opens in a new tab)",
                            )}
                        />
                    </AppStorePromoPill>
                ) : null,
                onPlay: () => {
                    trackEvent("landing_jigsaw_hero_play", { storyId: jigsawMeta.storyId });
                    navigate(`/puzzle/play-jigsaw/${encodeURIComponent(jigsawMeta.storyId)}`);
                },
                placeholderIcon: ExtensionIcon,
                preview: jigsawMeta.imageUrl ? (
                    <JigsawPreview
                        imageUrl={jigsawMeta.imageUrl}
                        pieces={jigsawMeta.piecePaths}
                        svgPathFallback={jigsawMeta.svgPath}
                    />
                ) : null,
                colorBaskets: jigsawColorBaskets,
            },
        ];
    }, [
        jigsawMeta,
        jigsawColorBaskets,
        t,
        navigate,
        playNowLabel,
        dailyFreeBadge,
        premiumBadge,
    ]);

    const wordCards = useMemo(() => {
        const cards = [];
        for (const slot of WORD_PUZZLE_SLOTS) {
            const featured = wordPuzzles[slot.puzzleType];
            const meta = featured?.meta;
            if (!meta) continue;
            let crosswordSubtitle = null;
            let subtitleDense = false;
            if (slot.puzzleType === "CROSSWORD" && featured?.row) {
                const teaser = buildCrosswordClueTeaserEntries(featured.row);
                if (teaser) {
                    crosswordSubtitle = (
                        <CrosswordClueTeaserDescription
                            teaser={teaser}
                            acrossLabel={t("puzzle.playCross.across")}
                            downLabel={t("puzzle.playCross.down")}
                        />
                    );
                    subtitleDense = true;
                }
            }

            const wordFreeTier = Boolean(meta.isFreeTier);
            cards.push({
                key: slot.id,
                puzzleKind: slot.puzzleKind,
                isFreeTier: wordFreeTier,
                badgeLabel: wordFreeTier ? dailyFreeBadge : premiumBadge,
                subtitleKey: slot.subtitleKey,
                subtitle: crosswordSubtitle,
                subtitleDense,
                playAriaKey: slot.playAriaKey,
                morePath: slot.morePath,
                title: meta.title,
                gridSize: null,
                levelLabel: formatDifficultyLabel(meta.level, t),
                publishedAt: meta.publishedAt,
                playLabel: solveLabel,
                onPlay: () => handleWordPuzzlePlay(meta, slot.trackEventName),
                placeholderIcon: slot.placeholderIcon,
                preview: buildWordPuzzleFeaturedPreview(featured.row, slot.puzzleType),
            });
        }
        return cards;
    }, [wordPuzzles, t, handleWordPuzzlePlay, solveLabel, dailyFreeBadge, premiumBadge]);

    const cardRenderOpts = { t, moreLabel, lang };
    const showSliding = slidingLoading || slidingCards.length > 0;

    return (
        <Box sx={{ width: "100%" }}>
            {showSliding ? (
                <Box
                    component="section"
                    aria-labelledby="landing-section-sliding-heading"
                    sx={SECTION_SX}
                >
                    <Typography
                        id="landing-section-sliding-heading"
                        component="h2"
                        sx={SECTION_HEADLINE_SX}
                    >
                        {t("landing.section-sliding-headline", "Sliding Puzzles")}
                    </Typography>
                    {slidingLoading && slidingCards.length === 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress size={36} sx={{ color: "#3BA666" }} />
                        </Box>
                    ) : (
                        <Box sx={CARD_FEED_SX}>
                            {slidingCards.map((card) => renderFeaturedCard(card, cardRenderOpts))}
                        </Box>
                    )}
                </Box>
            ) : null}

            <Box
                component="section"
                aria-labelledby="landing-section-jigsaw-heading"
                sx={SECTION_SX}
            >
                <Typography
                    id="landing-section-jigsaw-heading"
                    component="h2"
                    sx={SECTION_HEADLINE_SX}
                >
                    {t("landing.section-jigsaw-headline", "Jigsaw Puzzles")}
                </Typography>
                <Box sx={CARD_FEED_SX}>
                    {jigsawLoading && jigsawCards.length === 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={36} sx={{ color: "#3BA666" }} />
                        </Box>
                    ) : (
                        jigsawCards.map((card) => renderFeaturedCard(card, cardRenderOpts))
                    )}
                    <LandingColorBasketCard embedded />
                </Box>
            </Box>

            <Box
                component="section"
                aria-labelledby="landing-section-word-heading"
                sx={SECTION_SX}
            >
                <Typography
                    id="landing-section-word-heading"
                    component="h2"
                    sx={SECTION_HEADLINE_SX}
                >
                    {t("landing.section-word-headline", "Word Puzzles")}
                </Typography>
                <Box sx={CARD_FEED_SX}>
                    <LandingWordWheelSection embedded />
                    {wordPuzzlesLoading && wordCards.length === 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={36} sx={{ color: "#3BA666" }} />
                        </Box>
                    ) : (
                        wordCards.map((card) => renderFeaturedCard(card, cardRenderOpts))
                    )}
                </Box>
            </Box>
        </Box>
    );
}

